import { FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AnyAction } from '@reduxjs/toolkit';
import { AppState, StorageService, Logger } from '@nextgen-web-framework/core';
import {
  updateMyShopSliceReq,
  MyShopStatusEnum,
  updateMyListCreateState,
  InitialMyShopState,
  MyShop,
  getProductCmsDataTransformer,
  ICmsData,
} from '@commerce-myshop-webcomponents/sdk';
import { ErrorCard, Errors } from '@commerce-webcomponents/ui';
import { Theme, useToast, Notification, useTranslation } from '@nextgen-web-framework/all';
import { ListContainer } from '@commerce-cart-webcomponents/ui-composite';
import SharingBanner from '../sharing-banner/SharingBanner';
import Welcome from '../welcome/Welcome';
import ShopHeader from '../shop-header/ShopHeader';
import { SHOPLANDING_REDIRECTION_URL } from '../../config';
import FeatureProductLanding from '../feature-product-landing/FeatureProductLanding';
import logObject from '../../../logObject';
import { ShopLandingProps } from './types/propTypes';
import styles from './ShopLanding.module.scss';

const getToastMessage = (shopState: InitialMyShopState, toastMessage: { [x: string]: string }) => {
  let msg: string;
  let theme: Theme;
  let show: boolean;
  switch (true) {
    case shopState.updateMyShopStatusSuccess:
      msg = toastMessage.shopLandingSuccess;
      theme = Theme.SUCCESS;
      show = true;
      break;
    case shopState.updateMyShopStatusError:
      msg = toastMessage.shopLandingFail;
      theme = Theme.ERROR;
      show = true;
      break;
    case shopState.updateFeatureProductSuccess:
      msg = toastMessage.featureProductSuccess;
      theme = Theme.SUCCESS;
      show = true;
      break;
    default:
      msg = toastMessage.shopLandingFatalError;
      theme = Theme.ERROR;
      show = false;
      break;
  }
  return { msg, theme, show };
};

const getShopStateStatus = (shopState: InitialMyShopState, data: MyShop) =>
  shopState.shop?.status || data.status || 'ACTIVE';

const getShopStateIsFirstTimer = (shopState: InitialMyShopState, data: MyShop) =>
  shopState.shop?.isFirstTimer || data.isFirstTimer || false;

const getShopStateSelectedFeatureProduct = (shopState: InitialMyShopState, data: MyShop) =>
  shopState.shop?.featureProducts || data.featureProducts || [];

const getShopStateHasError = (shopState: InitialMyShopState) => shopState.getMyShopStatusError;

interface ReceiverViewProps {
  featureProductIds: string[];
  maxAllowableSelection: number;
  welcomeRedirect: () => void;
  isReceiver?: boolean;
  version: number;
  selectedFeatureProduct: string[];
  data: MyShop;
}

const ReceiverView: FC<ReceiverViewProps> = (props: ReceiverViewProps): React.JSX.Element => {
  const logger = Logger.getInstance(logObject('ShopLanding.ReceiverView'));
  try {
    const [listCount, setListCount] = useState<number>(-1);
    if (!props.isReceiver) {
      return (
        <div>
          <FeatureProductLanding
            featureProductIds={props.featureProductIds}
            maxAllowableSelection={props.maxAllowableSelection}
            version={props.version}
            selectedFeatureProducts={props.selectedFeatureProduct}
          />
          <ListContainer
            isReceiver={props.isReceiver}
            onListCountChange={setListCount}
            profileId={props.data.profileId}
            accountId={props.data.accountId}
          />
        </div>
      );
    } else {
      if ((props.selectedFeatureProduct && props.selectedFeatureProduct.length > 0) || listCount !== 0) {
        return (
          <div>
            {props.selectedFeatureProduct && props.selectedFeatureProduct.length > 0 && (
              <FeatureProductLanding
                featureProductIds={props.featureProductIds}
                version={props.version}
                maxAllowableSelection={props.maxAllowableSelection}
                isReceiver={props.isReceiver}
                selectedFeatureProducts={props.selectedFeatureProduct}
              />
            )}
            {listCount !== 0 && (
              <ListContainer
                isReceiver={props.isReceiver}
                onListCountChange={setListCount}
                profileId={props.data.profileId}
                accountId={props.data.accountId}
              />
            )}
          </div>
        );
      } else {
        return <Welcome imageUrl="/images/Welcome.png" onClick={props.welcomeRedirect} />;
      }
    }
  } catch (error) {
    logger.error('ShopLanding.ReceiverView', error);
    throw error;
  }
};

const ShopLanding: FC<ShopLandingProps> = (props: ShopLandingProps): React.JSX.Element => {
  const logger = Logger.getInstance(logObject('ShopLanding.ShopLanding'));
  try {
    const t = useTranslation();
    const { data, cmsData, welcomeRedirect, isReceiver } = props;

    const transformedCMS = getProductCmsDataTransformer(cmsData as ICmsData);
    const featureProductIds = transformedCMS?.products?.map((item) => item.product_id) || [];
    const maxAllowableSelection = transformedCMS?.max_products || 0;

    const dispatch = useDispatch();

    const shopState = useSelector((state: AppState) => state.commerceMyshop);
    if (!isReceiver) {
      StorageService.getLocalStorage().store('ShopId', shopState.shop?.shopId as string);
    }
    const status = getShopStateStatus(shopState, data);
    const version = shopState.shop?.version || data.version;
    const isFirstTimer = getShopStateIsFirstTimer(shopState, data);
    const selectedFeatureProduct = getShopStateSelectedFeatureProduct(shopState, data);
    const shopStateHasError = getShopStateHasError(shopState);
    const config = ConfigService.getConfigs();
    const ASSETS_WEB_BASE_PATH = config.DEFAULT_HOST_URL;
    const toastMessage = {
      shopLandingFatalError: t('myshop.ShopLanding.fatalError'),
      shopLandingSuccess: t('myshop.ShopLanding.savedSuccess'),
      shopLandingFail: t('myshop.ShopLanding.savedFail'),
      featureProductSuccess: t('myshop.ShopLanding.featureProductSuccess'),
    };

    const updateMyShopStatus = () => {
      dispatch(
        updateMyShopSliceReq({
          payload: {
            status: status === MyShopStatusEnum.Active ? MyShopStatusEnum.Inactive : MyShopStatusEnum.Active,
            version: version,
            isFirstTimer: false,
          } as unknown as BodyInit,
        }) as unknown as AnyAction,
      );
    };

    const { addToast, deleteToast } = useToast();
    useEffect(() => {
      const { msg, theme, show } = getToastMessage(shopState, toastMessage);
      if (show) {
        const id = 'list_create_toast';
        deleteToast('list_create_toast');

        addToast({
          id,
          timeout: 5000,
          content: (
            <Notification
              data-testid="myshop-landing-toaster-test-id"
              message={msg}
              theme={theme}
              onCloseClick={() => deleteToast(id)}
            />
          ),
        });
      }
      dispatch(updateMyListCreateState());
    }, [shopState.updateMyShopStatusSuccess, shopState.updateMyShopStatusError, shopState.updateFeatureProductSuccess]);

    if (data && data.loginBlocked) {
      return <ErrorCard type={Errors.NotFound} onActionClick={welcomeRedirect} />;
    }

    return shopStateHasError ? (
      <ErrorCard type={Errors.General} onActionClick={welcomeRedirect} />
    ) : (
      <div className={styles['myshop-landing-container']} data-testid="myshop-landing-container-test-id">
        <SharingBanner
          status={status}
          onUpdateStatus={updateMyShopStatus}
          isReceiver={isReceiver}
          isFirstTimer={isFirstTimer}
        />
        <ShopHeader
          shopName={data.shopName || ''}
          shopDescription={data.shopDescription || ''}
          shopCode={data.shopCode}
          bannerUrl={
            data.bannerInfo?.bannerId
              ? `${ASSETS_WEB_BASE_PATH}/${data.bannerInfo?.bannerId}`
              : '/images/AmwayProfileBanner.webp'
          }
          profileUrl={
            data.profilePicture?.imageId
              ? `${ASSETS_WEB_BASE_PATH}/${data.profilePicture?.imageId}`
              : '/images/AmwayProfileImage.svg'
          }
          socialInfo={data.socialInfo}
          status={status}
          redirectionUrl={`${SHOPLANDING_REDIRECTION_URL}`}
          isReceiver={isReceiver}
          contactInfo={data.contactInfo}
        />
        <ReceiverView
          featureProductIds={featureProductIds}
          maxAllowableSelection={maxAllowableSelection}
          welcomeRedirect={welcomeRedirect}
          isReceiver={isReceiver}
          version={version}
          selectedFeatureProduct={selectedFeatureProduct}
          data={data}
        />
      </div>
    );
  } catch (error) {
    logger.error('ShopLanding.ShopLanding', error);
    throw error;
  }
};

export default ShopLanding;
